import React, { useState, useEffect } from 'react';
import { reelDiscovery } from '../api';
import PromoCard from './PromoCard';

export default function ForYouFeed({ user, onPlay }) {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [personalized, setPersonalized] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [error, setError] = useState(null);

  const loadPage = async (cursor) => {
    try {
      const data = await reelDiscovery.getForYou(20, cursor);
      const page = data.promos || [];
      // Pages are re-ranked per request, so an item can legitimately reappear
      // on a later page — dedupe rather than rendering duplicate keys.
      setPromos(prev => {
        if (cursor == null) return page;
        const seen = new Set(prev.map(p => p.id));
        return [...prev, ...page.filter(p => !seen.has(p.id))];
      });
      setPersonalized(data.personalized || false);
      setNextCursor(data.nextCursor ?? null);
      setError(null);
      return page;
    } catch (err) {
      setError(err.message || 'Failed to load recommendations');
      return [];
    }
  };

  useEffect(() => {
    setLoading(true);
    loadPage(null).finally(() => setLoading(false));
  }, []);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    await loadPage(nextCursor);
    setLoadingMore(false);
  };

  if (loading) {
    return (
      <div style={{ padding: 'clamp(16px, 3vw, 32px) clamp(16px, 3vw, 40px)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 20 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{ borderRadius: 20, background: 'white', overflow: 'hidden', animation: 'pulse 1.5s ease infinite' }}>
              <div style={{ height: 300, background: '#eee' }} />
              <div style={{ padding: 16 }}>
                <div style={{ height: 16, background: '#eee', borderRadius: 4, marginBottom: 8 }} />
                <div style={{ height: 12, background: '#eee', borderRadius: 4, width: '60%' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // A failed request used to be indistinguishable from having no
  // recommendations, leaving no way to recover but a full page reload.
  if (error && !promos.length) {
    return (
      <div style={{ padding: 'clamp(16px, 3vw, 32px) clamp(16px, 3vw, 40px)', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
        <h3 style={{ color: '#fff', fontSize: 20, marginBottom: 8 }}>Couldn’t load your feed</h3>
        <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>{error}</p>
        <button
          onClick={() => { setLoading(true); loadPage(null).finally(() => setLoading(false)); }}
          style={{
            background: 'linear-gradient(135deg,#4ecb8d,#2f9e6b)', color: 'white', border: 'none',
            padding: '12px 28px', borderRadius: 50, cursor: 'pointer',
            fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 700,
          }}
        >Try again</button>
      </div>
    );
  }

  if (!promos.length) {
    return (
      <div style={{ padding: 'clamp(16px, 3vw, 32px) clamp(16px, 3vw, 40px)', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🎯</div>
        <h3 style={{ color: '#fff', fontSize: 20, marginBottom: 8 }}>No recommendations yet</h3>
        <p style={{ color: '#666', fontSize: 14 }}>Like some reels and follow creators to get personalized recommendations.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 'clamp(16px, 3vw, 32px) clamp(16px, 3vw, 40px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: '#4ecb8d', fontWeight: 600 }}>
          {personalized ? '🎯 Personalized for you' : '🔥 Trending reels'}
        </span>
      </div>
      <div style={{ columns: 'auto 240px', columnGap: 20 }}>
        {promos.map((promo, i) => (
          <PromoCard
            key={promo.id}
            promo={promo}
            currentUser={user}
            onClick={() => onPlay && onPlay(promos, i)}
          />
        ))}
      </div>
      {error && (
        <p style={{ textAlign: 'center', marginTop: 16, color: '#e28f8f', fontSize: 13 }}>{error}</p>
      )}
      {nextCursor != null && (
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button
            onClick={loadMore}
            disabled={loadingMore}
            style={{
              background: 'linear-gradient(135deg,#4ecb8d,#2f9e6b)', color: 'white', border: 'none',
              padding: '12px 28px', borderRadius: 50, cursor: loadingMore ? 'default' : 'pointer',
              fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 700,
            }}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
